import type { RobotSummary } from '@/models/RobotSummary';
import type { RobotDetails } from '@/models/RobotDetails';
import type { RobotRegistration, UpdateRobotGroup } from '@/models/Robot';
import CoreApi from '@/api/CoreApi';

export async function loadRobots(groupUuid: string): Promise<RobotSummary[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robots/control-units/${groupUuid}`,
    );
    return await response.data;
  } catch (_error) {
    throw new Error('Failed to fetch robots');
  }
}

export async function loadRobotDetails(
  serialNumber: string,
): Promise<RobotDetails> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robots/v2/stats/${serialNumber}`,
    );
    const robotDetails: RobotDetails = response.data;
    const modulesOrder = robotDetails.robotComposition.map(
      (mod) => mod.moduleSerialNumber,
    );
    robotDetails.modules.sort(
      (a, b) =>
        modulesOrder.indexOf(a.moduleSerialNumber) -
        modulesOrder.indexOf(b.moduleSerialNumber),
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error);
    throw new Error(`Failed to load robot details: ${error.message}`);
  }
}

export async function loadRobotsSerialNumbers(): Promise<string[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      '/robots/all/serial-numbers',
    );
    return await response.data;
  } catch (_error) {
    throw new Error('Failed to fetch robots');
  }
}

export async function patchRobotName(
  name: string,
  uuid: string,
): Promise<void> {
  try {
    await CoreApi.axiosClient.patch('/robots/control-units', {
      uuid,
      newName: name,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error);
    throw new Error(`Failed to patch robot: ${error.message}`);
  }
}

export async function postRegisterRobot(
  registration: RobotRegistration,
): Promise<void> {
  try {
    await CoreApi.axiosClient.post('/robots/control-units', registration);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error);
    throw new Error(`Failed to register robot: ${error.message}`);
  }
}

export async function updateRobotGroup(
  newRobotGroup: UpdateRobotGroup,
): Promise<void> {
  try {
    await CoreApi.axiosClient.patch('/robots/group', newRobotGroup);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error);
    throw new Error(`Failed to update robot group: ${error.message}`);
  }
}
